import AdsContainer from "@components/Blocks/Container/AdsContainer";
import {
  CreateLoadMoreHandler,
  CreateLoadMoreHandlerProps,
} from "@components/Modules/LoadMore/CreateLoadMoreHandler";
import LoadMore from "@components/Modules/LoadMore/LoadMore";
import { LOAD_MORE_CTA_COPY } from "@config/ctaCopy";
import {
  CompanionAdsLb,
  CompanionAdsLb1,
} from "@elements/Advertisement/variant/CompanionAdsLb";
import { getTagsName } from "@elements/GAData/GADataHelper";
import { LabelCardIconVariety } from "@elements/Icon/LabelCardIcon";
import Outbrain from "@elements/OutbrainAds/Outbrain";
import fetchBHApi from "@helper/fetchBHApi";
import { BH_BASE_URL } from "@helper/getEnvVariables";
import { removeTrailingSlash } from "@helper/removeTrailingLeadingSlash";
import { isArrayWithElements, isClientSide } from "@helper/utils";
import useVisibleArticle from "@hooks/useVisibleArticle";
import { OKTAUserTypeEnum } from "@src/app/store/useOKTAUserStore";
import { ProcessedArticleData } from "@transformer/useOSResponse";
import { AdTargetingType, ArticleAdTargetKeys } from "@typings/Ads.d";
import {
  FetchMoreArticlesFunction,
  MoreArticlesRequestData,
} from "@typings/MoreArticlesApi";
import Cookies from "js-cookie";
import React, { useEffect, useState } from "react";

import ArticleContentCard from "./ArticleContentCard";
import ArticleContentCardNew from "./ArticleLayout/ArticleContentCardNew";
import ArticleElementWrapper from "./Components/ArticleElementWrapper";

type ArticleInfiniteScrollProps = {
  mainArticle: ProcessedArticleData;
  latestRegisteredArticle: ProcessedArticleData[];
  onNewArticle: (
    article: ProcessedArticleData,
    pageNumber: number,
    viewPortChange: boolean,
  ) => void;
  isOutbrainEnabled: boolean;
  PubPageAdTarget: AdTargetingType<ArticleAdTargetKeys, string>;
  newDesign?: boolean;
};

export type ArticleVariantData = {
  payload: {
    article: {
      variant: string;
    };
  };
};

export default function ArticleInfiniteScroll({
  mainArticle,
  latestRegisteredArticle,
  onNewArticle,
  isOutbrainEnabled = false,
  PubPageAdTarget,
  newDesign,
}: ArticleInfiniteScrollProps) {
  const [hasMoreStories, setHasMoreStories] = useState<boolean>(true);
  const [renderedArticleIds, setRenderedArticleIds] = useState(
    new Set<string>([mainArticle.id]),
  );
  const [loadMoreData, setLoadMoreData] = useState<ProcessedArticleData[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [visibleArticleId, setVisibleArticleId] = useState<string>(
    mainArticle.id,
  );

  // Trigger useVisibleArticle when LoadMoreData changes
  const visibleArticleIdFromHook = useVisibleArticle(visibleArticleId);
  const [currentPath, setCurrentPath] = useState<string>(
    mainArticle.urlPath || "",
  );

  const filterArticlesBasedOn = mainArticle.parentSection
    ? mainArticle.linkToSection.split("/").at(-1)
    : mainArticle.uniqueSectionName;

  const fetchNextStoryData = async (
    requestData: MoreArticlesRequestData,
  ): Promise<ProcessedArticleData[]> => {
    const queryParams = new URLSearchParams({
      section: requestData.filterArticlesBasedOn as string,
      excludeIds: requestData.excludeIds as string,
    }).toString();
    const response: ProcessedArticleData[] = await fetchBHApi(
      `get-article?${queryParams}`,
      "GET",
    );
    console.log("debug fetched get-article data");
    const userType = Cookies.get("mySPHUserType");
    if (
      response.length > 0 &&
      response[0].urlPath &&
      response[0].contentAccess === LabelCardIconVariety.FREE &&
      userType === OKTAUserTypeEnum.ANONYMOUS
    ) {
      console.log("debug variant call condition is success");
      try {
        const contextJsonUrl = `${removeTrailingSlash(BH_BASE_URL)}${response[0].urlPath}/context.json`;
        const contextResponse = await fetch(contextJsonUrl, {
          method: "GET",
        });
        if (!contextResponse.ok) {
          throw new Error(
            `Failed to fetch context JSON: ${contextResponse.statusText}`,
          );
        }
        let additionalResponse;
        try {
          additionalResponse =
            (await contextResponse.json()) as ArticleVariantData;
        } catch (error) {
          throw new Error("Failed to parse context JSON.");
        }
        response[0].variant =
          additionalResponse.payload.article.variant || "default custom";
        console.log("debug variant from api", response[0].variant);
      } catch (error) {
        response[0].variant = "default error";
        console.error("Error loading or processing context.json:", error);
      }
      // response[0].title = `${response[0].variant}: ${response[0].title}`;
    }

    return response;
  };

  const handleLoadMore = async () => {
    const lastLoadedArticle =
      loadMoreData.length === 0 ? mainArticle : loadMoreData.at(-1);
    const requestData: MoreArticlesRequestData = {
      filterArticlesBasedOn,
      excludeIds: [...renderedArticleIds].join(","),
    };

    const fetchFunction: FetchMoreArticlesFunction = fetchNextStoryData;
    const updateGADataForArticle = true;

    const createLoadMoreProps: CreateLoadMoreHandlerProps = {
      requestData,
      fetchFunction,
      setHasMoreStories,
      setRenderedArticleIds,
      setLoadMoreData,
      setPageNumber,
      loadMoreData,
      renderedArticleIds,
      lastLoadedArticle,
      pageSize: 1,
      pageNumber,
      updateGADataForArticle,
    };

    const response = await CreateLoadMoreHandler(createLoadMoreProps);

    if (isArrayWithElements(response)) {
      const article = response[0];
      // Update the URL and visibleArticleId when the article is loaded
      setCurrentPath(article.urlPath || "");
      setVisibleArticleId(article.id);
      onNewArticle(article, pageNumber + 1, false);
    } else {
      setHasMoreStories(false);
    }

    return response;
  };

  useEffect(() => {
    if (!isClientSide || !visibleArticleId) return;

    const allArticles = [mainArticle, ...loadMoreData];
    const visibleArticleIndex = allArticles.findIndex(
      (article) => article.id === visibleArticleId,
    );

    const articleData =
      visibleArticleIndex !== -1 ? allArticles[visibleArticleIndex] : undefined;

    if (articleData) {
      const newPath = articleData.urlPath;
      if (newPath && newPath !== currentPath) {
        window.history.pushState("article", articleData.title, newPath);
        setCurrentPath(newPath);
        onNewArticle(articleData, visibleArticleIndex, true); // Notify parent component
      }
    }
  }, [visibleArticleId, loadMoreData, mainArticle, onNewArticle, currentPath]);

  useEffect(() => {
    if (!isClientSide) return;
    if (visibleArticleIdFromHook) {
      setVisibleArticleId(visibleArticleIdFromHook);
    }
  }, [visibleArticleIdFromHook]);

  return (
    <div>
      {[...loadMoreData].map((context, index) => (
        <React.Fragment key={context.id}>
          {newDesign ? (
            <AdsContainer className="my-6 lg:my-8">
              <CompanionAdsLb1
                uniqueSectionName={context.adsUniqueSectionName}
                stripeBackground={false}
                index={index + 1}
                gsChannels={context.gsChannels}
                articleSlotTarget={[
                  { key: ArticleAdTargetKeys.ARTICLE_ID, value: context.id },
                  {
                    key: ArticleAdTargetKeys.TAGS,
                    value: getTagsName(context.tags || []).toLowerCase(),
                  },
                  PubPageAdTarget,
                ]}
              />
            </AdsContainer>
          ) : (
            <CompanionAdsLb
              uniqueSectionName={context.adsUniqueSectionName}
              index={index + 1}
              gsChannels={context.gsChannels}
              articleSlotTarget={[
                { key: ArticleAdTargetKeys.ARTICLE_ID, value: context.id },
                {
                  key: ArticleAdTargetKeys.TAGS,
                  value: getTagsName(context.tags || []).toLowerCase(),
                },
                PubPageAdTarget,
              ]}
            />
          )}
          <div id={context.id} className="article-card">
            {newDesign ? (
              <>
                <ArticleContentCardNew
                  mainArticle={context}
                  latestRegisteredArticle={latestRegisteredArticle}
                  pageNumber={index + 1}
                  isInfiniteScrollArticle={true}
                  gsChannels={context.gsChannels}
                  articleSlotTarget={[
                    { key: ArticleAdTargetKeys.ARTICLE_ID, value: context.id },
                    {
                      key: ArticleAdTargetKeys.TAGS,
                      value: getTagsName(context.tags || []).toLowerCase(),
                    },
                    PubPageAdTarget,
                  ]}
                  index={index}
                  visibleArticleId={visibleArticleId}
                />
                <ArticleElementWrapper>
                  {isOutbrainEnabled && context.urlPath && (
                    <div className="mt-4 lg:mt-6 border-t-2 border-blue-400">
                      <Outbrain pathName={context.urlPath} />
                    </div>
                  )}
                </ArticleElementWrapper>
              </>
            ) : (
              <ArticleContentCard
                mainArticle={context}
                latestRegisteredArticle={latestRegisteredArticle}
                pageNumber={index + 1}
                isInfiniteScrollArticle={true}
                isOutbrainEnabled={isOutbrainEnabled}
                gsChannels={context.gsChannels}
                articleSlotTarget={[
                  { key: ArticleAdTargetKeys.ARTICLE_ID, value: context.id },
                  {
                    key: ArticleAdTargetKeys.TAGS,
                    value: getTagsName(context.tags || []).toLowerCase(),
                  },
                  PubPageAdTarget,
                ]}
                index={index}
                visibleArticleId={visibleArticleId}
              />
            )}
          </div>
        </React.Fragment>
      ))}
      <ArticleElementWrapper className="mt-12 mb-6 lg:mt-20 lg:mb-10">
        <LoadMore
          rootClassName="my-4"
          onLoadMore={handleLoadMore}
          hasMore={hasMoreStories}
          loadText={LOAD_MORE_CTA_COPY}
          isArticlePage={true}
          virtualPVTitle={mainArticle.title}
          pageNumber={pageNumber}
          autoLoadOnMobile={newDesign}
        />
      </ArticleElementWrapper>
    </div>
  );
}
